import {
    apiQueryKey,
    useDeleteContract,
    useGenerateContract,
    useUpdateContract,
    useUpdateContractNumber,
    useUpdateMarkingFormData,
    useUploadContractFile,
} from '@/shared/api'
import {
    useCreateCustomContract,
    useUpdateCustomContract,
} from '@/shared/api/ord/customDocuments.hooks'
import { ORDMarkingFormData } from '@synapex/prisma/client'
import { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'
import { useIsMutating, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { toast } from 'react-toastify'

const handleAxiosError = (error: unknown, defaultMessage: string) => {
    if (isAxiosError(error)) {
        const data = error.response?.data as { message?: string } | undefined
        if (data?.message) {
            toast.error(data.message)
        } else {
            toast.error(defaultMessage)
        }
    }
}

export function useContractMutations(
    markingFormData: ORDMarkingFormData,
    wwld: WorkspaceWithLinkDescriptor
) {
    const queryClient = useQueryClient()

    const updateMarkingFormData = useUpdateMarkingFormData(wwld, {
        mutationKey: ['mutation-update-marking-form-data'],
    })

    const globalUpdateMarkingFormDataPending = !!useIsMutating({
        mutationKey: ['mutation-update-marking-form-data'],
    })

    const generateContract = useGenerateContract(wwld, {
        onError(error) {
            handleAxiosError(error, 'Ошибка при создании договора')
        },
        mutationKey: ['mutation-generate-contract'],
    })

    const globalGenerateContractPending = !!useIsMutating({
        mutationKey: ['mutation-generate-contract'],
    })

    const uploadContractFile = useUploadContractFile(wwld)

    const updateContract = useUpdateContract(wwld, {
        mutationKey: ['mutation-update-contract'],
        onError(error) {
            handleAxiosError(error, 'Ошибка при создании договора')
        },
    })

    const globalUpdateContractPending = !!useIsMutating({
        mutationKey: ['mutation-update-contract'],
    })

    const updateContractNumber = useUpdateContractNumber(wwld, {
        mutationKey: ['mutation-update-contract-number'],
        onError(error: unknown) {
            handleAxiosError(error, 'Не удалось обновить номер договора')
        },
    })

    const globalUpdateContractNumberPending = !!useIsMutating({
        mutationKey: ['mutation-update-contract-number'],
    })

    const deleteContract = useDeleteContract(wwld, {
        mutationKey: ['mutation-delete-contract'],
    })

    const globalDeleteContractPending = !!useIsMutating({
        mutationKey: ['mutation-delete-contract'],
    })

    const createCustomContract = useCreateCustomContract(wwld)
    const updateCustomContract = useUpdateCustomContract(wwld)

    function invalidateMarkingFormDataQueries() {
        void queryClient.invalidateQueries({
            queryKey: apiQueryKey.markingFormData(),
            predicate: (query) => {
                const queryData = query.state.data as {
                    draft_contract_id?: number
                    site_link_id?: number
                    invite_link_id?: number
                }
                return (
                    queryData.draft_contract_id ===
                        markingFormData.draft_contract_id &&
                    (markingFormData.site_link_id !== queryData.site_link_id ||
                        markingFormData.invite_link_id !==
                            queryData.invite_link_id)
                )
            },
        })
    }

    function invalidateContractsQueries() {
        if (!markingFormData.ord_contragent_id) {
            return
        }
        void queryClient.invalidateQueries({
            queryKey: apiQueryKey.contracts(
                wwld,
                markingFormData.ord_contragent_id
            ),
        })
    }

    return {
        updateMarkingFormData,
        globalUpdateMarkingFormDataPending,
        generateContract,
        globalGenerateContractPending,
        uploadContractFile,
        updateContract,
        globalUpdateContractPending,
        updateContractNumber,
        globalUpdateContractNumberPending,
        deleteContract,
        globalDeleteContractPending,
        createCustomContract,
        updateCustomContract,
        invalidateMarkingFormDataQueries,
        invalidateContractsQueries,
    }
}
